import React from "react";
import {
	Box,
	Typography,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	LinearProgress,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { formatDate, getCodeDescription } from "../../utils/functions";
import { useSelector } from "react-redux";

// ─── Service Due Tab
const ServiceDueTab = ({ svcStatuses, vehicle, workOrders = [], classes }) => {
	const theme = useTheme();
	const dictionary = useSelector((state) => state.dictionary.dictionary);

	const overdueCount = svcStatuses.filter(
		(s) => s.status === "Overdue",
	).length;
	const dueSoonCount = svcStatuses.filter(
		(s) => s.status === "Due Soon",
	).length;
	const okCount = svcStatuses.filter((s) => s.status === "OK").length;

	const getStatusColor = (status) => {
		switch (status) {
			case "Overdue":
				return theme.palette.error.main;
			case "Due Soon":
				return theme.palette.warning.main;
			case "OK":
				return theme.palette.success.main;
			default:
				return theme.palette.text.secondary;
		}
	};

	const fmtMi = (n) => (n != null ? `${Number(n).toLocaleString()} mi` : "—");

	return (
		<Box>
			{svcStatuses.length === 0 ? (
				<Box className={classes.emptyState}>
					<Box className={classes.emptyIcon}>⚙️</Box>
					<Typography>
						No service requirements assigned to this vehicle
					</Typography>
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ mt: 1 }}
					>
						Add service requirements in the vehicle edit dialog
					</Typography>
				</Box>
			) : (
				<>
					{/* Summary Text */}
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mb: 2 }}
					>
						{overdueCount} overdue · {dueSoonCount} due soon ·{" "}
						{okCount} current
					</Typography>

					{/* Service Requirements Table */}
					<Box className={classes.tableWrap}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Requirement</TableCell>
									<TableCell>Interval</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Last Serviced</TableCell>
									<TableCell>Last Odometer</TableCell>
									<TableCell>Current Odometer</TableCell>
									<TableCell>Progress</TableCell>
									<TableCell>Due In</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{svcStatuses.map(
									({
										req,
										status,
										dueIn,
										unit,
										lastServiceDate,
										lastOdometer,
										milesSince,
										daysSince,
									}) => {
										const interval =
											Number(req?.Value) || 0;
										const isMileage =
											unit === "mi" || unit === "miles";

										// Calculate values for display
										const usedValue = isMileage
											? milesSince || 0
											: daysSince || 0;
										const pct =
											interval > 0
												? Math.min(
														100,
														Math.round(
															(usedValue /
																interval) *
																100,
														),
													)
												: 0;

										// Calculate remaining (due in)
										const remaining = interval - usedValue;

										// Format display
										const currentOdometer =
											vehicle?.odometer || 0;
										const displayLastOdometer =
											lastOdometer ||
											(isMileage && lastServiceDate
												? currentOdometer -
													(milesSince || 0)
												: null);

										return (
											<TableRow
												key={req?.ptsCodeId || req?.id}
												sx={{
													bgcolor:
														status === "Overdue"
															? "rgba(239,68,68,0.04)"
															: status ===
																  "Due Soon"
																? "rgba(245,158,11,0.04)"
																: "transparent",
												}}
											>
												<TableCell
													sx={{ fontWeight: 600 }}
												>
													<Box
														sx={{
															display: "flex",
															alignItems:
																"center",
															gap: 1,
														}}
													>
														{status ===
															"Overdue" && (
															<Box
																sx={{
																	width: 8,
																	height: 8,
																	borderRadius:
																		"50%",
																	bgcolor:
																		theme
																			.palette
																			.error
																			.main,
																	animation:
																		"pulse 2s infinite",
																}}
															/>
														)}
														{getCodeDescription(req?.Code, dictionary.codePerVehicleServiceRequirements) || "—"}
													</Box>
												</TableCell>
												<TableCell>
													<Typography
														variant="caption"
														sx={{
															fontFamily:
																"monospace",
														}}
													>
														Every{" "}
														{interval.toLocaleString()}{" "}
														{req?.Unit || ""}
													</Typography>
												</TableCell>
												<TableCell>
													<Chip
														label={status}
														size="small"
														sx={{
															bgcolor: `${getStatusColor(status)}20`,
															color: getStatusColor(
																status,
															),
															fontWeight: 700,
															fontSize: 11,
														}}
													/>
												</TableCell>
												<TableCell>
													<Typography
														variant="caption"
														sx={{
															fontFamily:
																"monospace",
														}}
													>
														{lastServiceDate
															? formatDate(
																	lastServiceDate,
																)
															: "Never"}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography
														variant="caption"
														sx={{
															fontFamily:
																"monospace",
															color: displayLastOdometer
																? "text.primary"
																: "text.disabled",
														}}
													>
														{displayLastOdometer
															? fmtMi(
																	displayLastOdometer,
																)
															: "—"}
													</Typography>
												</TableCell>
												<TableCell>
													<Typography
														variant="caption"
														sx={{
															fontFamily:
																"monospace",
														}}
													>
														{fmtMi(currentOdometer)}
													</Typography>
												</TableCell>
												<TableCell
													sx={{ minWidth: 140 }}
												>
													<Box
														sx={{
															display: "flex",
															alignItems:
																"center",
															gap: 1,
														}}
													>
														<Box sx={{ flex: 1 }}>
															<LinearProgress
																variant="determinate"
																value={pct}
																sx={{
																	height: 8,
																	borderRadius: 4,
																	bgcolor:
																		"action.hover",
																	"& .MuiLinearProgress-bar":
																		{
																			bgcolor:
																				getStatusColor(
																					status,
																				),
																			borderRadius: 4,
																		},
																}}
															/>
														</Box>
														<Typography
															variant="caption"
															sx={{
																fontFamily:
																	"monospace",
																color: "text.secondary",
																minWidth: 32,
																textAlign:
																	"right",
															}}
														>
															{pct}%
														</Typography>
													</Box>
													{isMileage && (
														<Typography
															variant="caption"
															color="text.disabled"
															sx={{
																fontSize: 10,
																fontFamily:
																	"monospace",
															}}
														>
															{usedValue.toLocaleString()}{" "}
															/{" "}
															{interval.toLocaleString()}{" "}
															mi
														</Typography>
													)}
												</TableCell>
												<TableCell>
													<Typography
														variant="caption"
														sx={{
															fontFamily:
																"monospace",
															fontWeight: 700,
															color: getStatusColor(
																status,
															),
															display: "block",
														}}
													>
														{remaining < 0 ? (
															<>
																<span
																	style={{
																		color: theme
																			.palette
																			.error
																			.main,
																	}}
																>
																	{Math.abs(
																		remaining,
																	).toLocaleString()}{" "}
																	{isMileage
																		? "mi"
																		: "days"}{" "}
																	overdue
																</span>
															</>
														) : remaining === 0 ? (
															<span
																style={{
																	color: theme
																		.palette
																		.warning
																		.main,
																}}
															>
																Due now
															</span>
														) : (
															<>
																{remaining.toLocaleString()}{" "}
																{isMileage
																	? "mi"
																	: "days"}
															</>
														)}
													</Typography>
													{remaining > 0 &&
														isMileage && (
															<Typography
																variant="caption"
																color="text.disabled"
																sx={{
																	fontSize: 10,
																}}
															>
																≈{" "}
																{Math.round(
																	remaining /
																		50,
																)}{" "}
																days @ 50mi/day
															</Typography>
														)}
												</TableCell>
											</TableRow>
										);
									},
								)}
							</TableBody>
						</Table>
					</Box>

					{/* Legend */}
					<Box
						sx={{
							mt: 2,
							p: 1.5,
							bgcolor: "background.default",
							borderRadius: 1,
							border: "1px solid",
							borderColor: "divider",
							display: "flex",
							gap: 3,
							alignItems: "center",
						}}
					>
						<Typography
							variant="caption"
							fontWeight={700}
							color="text.secondary"
							sx={{
								textTransform: "uppercase",
								letterSpacing: "0.06em",
							}}
						>
							Legend:
						</Typography>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
							}}
						>
							<Box
								sx={{
									width: 12,
									height: 12,
									borderRadius: 1,
									bgcolor: `${theme.palette.error.main}20`,
									border: `1px solid ${theme.palette.error.main}`,
								}}
							/>
							<Typography
								variant="caption"
								color="error.main"
								fontWeight={600}
							>
								Overdue
							</Typography>
						</Box>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
							}}
						>
							<Box
								sx={{
									width: 12,
									height: 12,
									borderRadius: 1,
									bgcolor: `${theme.palette.warning.main}20`,
									border: `1px solid ${theme.palette.warning.main}`,
								}}
							/>
							<Typography
								variant="caption"
								color="warning.main"
								fontWeight={600}
							>
								Due Soon (&lt;20% remaining)
							</Typography>
						</Box>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.5,
							}}
						>
							<Box
								sx={{
									width: 12,
									height: 12,
									borderRadius: 1,
									bgcolor: `${theme.palette.success.main}20`,
									border: `1px solid ${theme.palette.success.main}`,
								}}
							/>
							<Typography
								variant="caption"
								color="success.main"
								fontWeight={600}
							>
								OK
							</Typography>
						</Box>
					</Box>
				</>
			)}

			{/* Add CSS for pulse animation */}
			<style>
				{`
                    @keyframes pulse {
                        0% { opacity: 1; }
                        50% { opacity: 0.4; }
                        100% { opacity: 1; }
                    }
                `}
			</style>
		</Box>
	);
};

export default ServiceDueTab;
